/**
 * Płace (P5, faza 2) — enumy, kontrakty API i kształt parametrów naliczeń.
 *
 * Zasady, które ten plik utrwala:
 * - kwoty ZAWSZE w groszach (`number` całkowity, prezentacja przez `formatPLN`),
 * - stawki ZAWSZE w punktach bazowych `bp` = 1/10000 (9,76 % = 976) — nigdy
 *   `float`, bo z ułamków dziesiętnych nie da się policzyć groszy powtarzalnie,
 * - ostrzeżenia i etykiety wychodzą z API jako KLUCZE i18n z parametrami,
 *   nie jako zdania (ten sam kontrakt ma asystent — patrz `assistant.ts`).
 */
import { isEmploymentContract, type ContractType } from './enums'
import type { InsuranceScope } from './registrations'
import type { TaxResidence } from './nonresident'
import type { SickAbsenceView } from './sickness'

/** Stawka w punktach bazowych: `bp / 10 000` (976 = 9,76 %). */
export type RateBp = number

export const BP_SCALE = 10_000

/** Rodzaj składnika paska (spec 01 §8b „Składniki wynagrodzeń"). */
export const PAYROLL_COMPONENT_KINDS = [
  'wynagrodzenie_zasadnicze',
  'premia',
  'nadgodziny',
  /**
   * Nadgodziny z dodatkiem 100 % (art. 151(1) § 1 pkt 1 i § 2 k.p.): praca
   * nadliczbowa w porze nocnej, w niedziele i święta niebędące dniami pracy
   * oraz nadwyżka nad przeciętną normą tygodniową. Osobny składnik, bo pasek
   * musi dać się przeliczyć — stawka jest inna niż przy 50 % (Etap 42).
   */
  'nadgodziny_100',
  'dodatek_nocny',
  /**
   * Ryczałt za nadgodziny (art. 151(1) § 4 k.p.) — zastępuje wynagrodzenie
   * wraz z dodatkiem za pracę nadliczbową u pracowników wykonujących stale
   * pracę poza zakładem. Godziny zostają w ewidencji i w licznikach; zmienia
   * się tylko sposób zapłaty (Etap 51, ADR-0052).
   */
  'ryczalt_nadgodziny',
  /** Ryczałt za pracę w porze nocnej (art. 151(8) § 2 k.p.) — Etap 51. */
  'ryczalt_nocny',
  /**
   * Dodatek 100 % za godziny pracy w niedzielę albo święto, za które nie
   * udzielono dnia wolnego w terminie (art. 151(11) § 2 i § 3 k.p.). Osobny
   * składnik od nadgodzin, bo te godziny nadgodzinami NIE są: nie wchodzą do
   * limitu z art. 151 § 3 ani do przeciętnej z art. 131 § 1 (Etap 49, ADR-0050).
   */
  'dodatek_niedzielny',
  /**
   * Wynagrodzenie za czas dyżuru, którego nie pokrył czas wolny (art. 151(5)
   * § 3 k.p.). Osobny składnik: stawka jest z osobistego zaszeregowania, bez
   * dodatku, a godziny dyżuru nie są czasem pracy — więc nie wchodzą ani do
   * nadgodzin, ani do limitów z art. 151 § 3 (Etap 50, ADR-0051).
   */
  'wynagrodzenie_za_dyzur',
  'wynagrodzenie_chorobowe',
  'zasilek_chorobowy',
  /**
   * Zasiłek macierzyński (art. 29–31 ustawy zasiłkowej) — osobny składnik, bo
   * to NIE ten sam przychód co zasiłek chorobowy: ulga dla młodych obejmuje
   * macierzyński (art. 21 ust. 1 pkt 148 PIT), a chorobowego nie, i formularz
   * PIT-11 ma dla niego własne wiersze 12–14 (Etap 29, ADR-0030).
   */
  'zasilek_macierzynski',
  'potracenie_chorobowe',
  'potracenie_nieobecnosc',
  'faktura_b2b',
  /** Jednorazowy ekwiwalent pieniężny (np. za urlop) — wpisują płace (Etap 14). */
  'ekwiwalent',
  /** Jednorazowe potrącenie z wynagrodzenia BRUTTO — kwota ujemna. */
  'potracenie_brutto',
  /**
   * Odprawa z art. 8 ustawy o zwolnieniach grupowych albo odprawa
   * emerytalno-rentowa z art. 92(1) k.p. (Etap 23). Kwotę system liczy, ale
   * o tym, czy się należy, decyduje człowiek — patrz ADR-0024.
   */
  'odprawa',
] as const
export type PayrollComponentKind = (typeof PAYROLL_COMPONENT_KINDS)[number]

/**
 * Składniki, które wolno DOPISAĆ ręcznie do okresu w weryfikacji (Etap 14).
 * Reszta pochodzi z faktów: z warunków umowy, ewidencji czasu i rejestru
 * zwolnień — ich nikt nie wpisuje z palca, bo wtedy pasek przestaje być
 * odtwarzalny z akt.
 */
export const PAYROLL_EXTRA_KINDS: readonly PayrollComponentKind[] = [
  'premia',
  'ekwiwalent',
  'odprawa',
  'potracenie_brutto',
]

/** Jednorazowy składnik okresu wpisany przez płace. */
export interface PayrollExtraView {
  readonly id: string
  readonly employeeId: string
  readonly employeeName: string
  readonly kind: PayrollComponentKind
  /** Kwota w groszach; potrącenie jest ujemne. */
  readonly amountGr: number
  readonly note: string | null
  readonly createdBy: string | null
  readonly createdAt: string
}

/**
 * Potrącenia zapisujemy jako kwoty UJEMNE — suma składników daje przychód
 * brutto bez dodatkowych reguł znaku. Ta lista mówi, które nimi są.
 */
export const PAYROLL_DEDUCTION_KINDS: readonly PayrollComponentKind[] = [
  'potracenie_nieobecnosc',
  'potracenie_chorobowe',
  'potracenie_brutto',
]

/**
 * Składniki, które NIE wchodzą do podstawy wymiaru składek na ubezpieczenia
 * społeczne — a więc również do podstawy wpłat do PPK, bo ta jest tożsama
 * z podstawą emerytalno-rentową (art. 26 ust. 1 ustawy o PPK).
 * Wynagrodzenie za czas niezdolności do pracy i zasiłki są z niej wyłączone
 * (§ 2 ust. 1 pkt 25 rozporządzenia MPiPS z 18.12.1998).
 */
export const PAYROLL_SOCIAL_EXEMPT_KINDS: readonly PayrollComponentKind[] = [
  'wynagrodzenie_chorobowe',
  'zasilek_chorobowy',
  'zasilek_macierzynski',
]

/**
 * Składniki wyłączone z podstawy wymiaru składki ZDROWOTNEJ. Zasiłek jest
 * świadczeniem z ubezpieczenia społecznego, więc składki zdrowotnej nie ma.
 * Wynagrodzenia chorobowego na tej liście NIE MA i to nie przeoczenie:
 * art. 81 ust. 5 ustawy o świadczeniach opieki zdrowotnej każe pominąć
 * wyłączenie wynagrodzenia za czas niezdolności do pracy — składka zdrowotna
 * jest od niego należna.
 */
export const PAYROLL_HEALTH_EXEMPT_KINDS: readonly PayrollComponentKind[] = [
  'zasilek_chorobowy',
  'zasilek_macierzynski',
]

/**
 * Składniki, do których nie stosuje się kosztów uzyskania przychodu. Zasiłek
 * jest przychodem z innych źródeł (art. 20 ust. 1 ustawy o PIT), a nie
 * przychodem ze stosunku pracy — kosztów pracowniczych przy nim nie ma.
 */
export const PAYROLL_KUP_EXEMPT_KINDS: readonly PayrollComponentKind[] = ['zasilek_chorobowy']

/**
 * Pigułka przy nietypowej pozycji (spec 01 §8b): `high` = „Sprawdź" (bursztyn),
 * `info` = „Do wglądu" (róż). Żadne ostrzeżenie nie blokuje wypłaty — blokują
 * wyłącznie braki danych zgłaszane jako błąd naliczenia.
 */
export const PAYROLL_WARNING_LEVELS = ['high', 'info'] as const
export type PayrollWarningLevel = (typeof PAYROLL_WARNING_LEVELS)[number]

/**
 * Czy osoba złożyła deklarację PPK — przystąpienie (`uczestnik`) albo
 * rezygnację (`rezygnacja`, art. 23 ust. 2 ustawy o PPK). Jedna reguła dla
 * pliku PPK, deklaracji i przelewu PPK: wpłaty pobrane od uczestnika, który
 * później zrezygnował, dalej idą do instytucji (przegląd Fali 1, #85).
 */
export function isPpkDeclared(status: PpkStatus | null): boolean {
  return status === 'uczestnik' || status === 'rezygnacja'
}

/**
 * Rodzaje ostrzeżeń — każdy ma klucz i18n `hrPayroll.warnings.<kind>` (tytuł)
 * oraz `…Desc` (nota). Kolejność jest kolejnością prezentacji.
 */
export const PAYROLL_WARNING_KINDS = [
  /** Parametry roku nie zostały potwierdzone przez księgowość. */
  'parametry_niepotwierdzone',
  /** Brak limitu rocznej podstawy („30-krotność") — składki bez ograniczenia. */
  'limit_rocznej_podstawy_nieustawiony',
  /** Okres ewidencji czasu pracy wciąż otwarty — nadgodziny mogą się zmienić. */
  'okres_czasu_pracy_otwarty',
  /** Brutto różni się od poprzedniego okresu o więcej niż próg. */
  'odchylenie_wynagrodzenia',
  /** Zatwierdzone nadgodziny bez stawki (brak kwoty w warunkach umowy). */
  'nadgodziny_bez_stawki',
  /** Przyjęte warunki umowy bez kwoty wynagrodzenia — nie ma z czego liczyć. */
  'brak_stawki_wynagrodzenia',
  /** Firma nie ma wpisanej składki wypadkowej — koszt pracodawcy jest niepełny. */
  'brak_stawki_wypadkowej',
  /**
   * Umowa do 200 zł u osoby z ulgą dla młodych. Ryczałt z art. 30 ust. 1 pkt 5a
   * i zwolnienie z art. 21 ust. 1 pkt 148 spotykają się rzadko, a wykładnia
   * ich zbiegu nie jest jednolita — silnik zostawia ulgę i mówi o tym wprost,
   * zamiast wybierać za człowieka (Etap 24, ADR-0025).
   */
  'ryczalt_a_ulga_dla_mlodych',
  /** Pracownik bez deklaracji PPK — naliczono wg ustawień domyślnych. */
  'brak_deklaracji_ppk',
  /** Wynagrodzenie poniżej minimalnego dla wymiaru etatu. */
  'ponizej_minimalnego',
  /** Praca w porze nocnej bez wpisanego minimalnego wynagrodzenia. */
  'dodatek_nocny_bez_minimalnego',
  /**
   * Zajęcie wynagrodzenia bez wpisanego minimalnego wynagrodzenia roku: kwoty
   * wolnej z art. 87(1) § 1 k.p. nie da się policzyć, więc z wynagrodzenia nie
   * zszedł ani grosz na tytuły, które ta kwota chroni. Alimentów to nie dotyczy
   * — ich kwota wolna nie chroni (ADR-0054).
   */
  'kwota_wolna_bez_minimalnego',
  /**
   * Kontroli „poniżej minimalnego" nie wykonano, bo minimalnego wynagrodzenia
   * nie ma w parametrach roku. Reguła obszaru: liczymy dalej, ale mówimy
   * o tym wprost (ADR-0010).
   */
  'kontrola_minimalnego_niewykonana',
  /**
   * Odprawa z art. 8 ustawy o zwolnieniach grupowych policzona bez pułapu
   * 15-krotności, bo minimalnego wynagrodzenia nie ma w parametrach roku —
   * kwota może przekraczać ustawowe maksimum z art. 8 ust. 4.
   */
  'odprawa_bez_pulapu',
  /**
   * Próg Funduszu Pracy i Funduszu Solidarnościowego (podstawa co najmniej
   * minimalnego wynagrodzenia w przeliczeniu na miesiąc — art. 259 ust. 1
   * ustawy o rynku pracy i służbach zatrudnienia, wcześniej art. 104 ust. 1
   * pkt 1 ustawy o promocji zatrudnienia) nie dał się sprawdzić, bo
   * minimalnego wynagrodzenia nie ma w parametrach roku. Składka policzona
   * w pełnej wysokości (#5, ADR-0054).
   */
  'fundusz_pracy_bez_minimalnego',
  /**
   * Lista utworzona WCZEŚNIEJ, z tym samym miesiącem wypłaty, nie jest
   * naliczona. Stan miesiąca — koszty uzyskania, kwota zmniejszająca i próg
   * FP — liczy się z list utworzonych wcześniej, więc ta lista policzyła je
   * tak, jakby tamtej nie było (ADR-0010 § 4).
   */
  'lista_wczesniejsza_nienaliczona',
  /**
   * Listy utworzone PÓŹNIEJ, z tym samym miesiącem wypłaty, mają już paski
   * policzone bez aktualnego stanu tej listy — trzeba je przeliczyć, a
   * zamknięte skorygować przez „Otwórz ponownie" (ADR-0014).
   */
  'lista_pozniejsza_do_przeliczenia',
  /** Brak daty urodzenia — nie wiadomo, czy stosować ulgę dla młodych. */
  'brak_daty_urodzenia',
  /** Miesiąc przekroczenia limitu ulgi dla młodych — podział przychodu do sprawdzenia. */
  'przekroczenie_ulgi_dla_mlodych',
  /** B2B: kwota z warunków umowy, do potwierdzenia fakturą. */
  'b2b_do_potwierdzenia_faktura',
  /**
   * Przychód należny za inny rok kalendarzowy niż rok wypłaty. ZUS wymaga wtedy
   * raportu ZUS RPA (art. 41 ust. 3 ustawy o systemie ubezpieczeń społecznych),
   * którego moduł deklaracji nie wystawia — mówimy o tym wprost.
   */
  'przychod_za_inny_rok',
  /** Pula 33 (14) dni wynagrodzenia chorobowego wyczerpana — dalej zasiłek. */
  'chorobowe_limit_wyczerpany',
  /** Zasiłek chorobowy wypłaca ZUS — do ZUS-u trzeba wysłać zaświadczenie Z-3. */
  'zasilek_wyplaca_zus',
  /** Dni niezdolności bez prawa do świadczenia (okres wyczekiwania z art. 4). */
  'chorobowe_bez_prawa',
  /** Podstawa z umowy, bo brak pełnego miesiąca historii (art. 37). */
  'podstawa_chorobowego_z_umowy',
  /** Nieznane dni chorobowego u poprzedniego pracodawcy w tym roku. */
  'chorobowe_dni_z_poprzedniej_pracy',
  /** Długość niezdolności zbliża się do okresu zasiłkowego (art. 8) — do sprawdzenia. */
  'okres_zasilkowy_do_sprawdzenia',
  /**
   * Okres zasiłkowy (182 albo 270 dni) WYCZERPANY — dni po nim nie dają ani
   * wynagrodzenia, ani zasiłku. Dalej idzie świadczenie rehabilitacyjne albo
   * renta, ale o nich orzeka ZUS, nie płatnik (Etap 21).
   */
  'okres_zasilkowy_wyczerpany',
  /** Roczny limit zasiłku opiekuńczego wyczerpany — dni bez prawa do zasiłku. */
  'limit_opiekunczego_wyczerpany',
  /**
   * Zasiłek macierzyński policzony ze stawki z rodzaju urlopu i wniosku.
   * Długości urlopów system nie ustala — wynikają z Kodeksu pracy i wniosku.
   */
  'zasilek_macierzynski_do_sprawdzenia',
  /**
   * Warunki umowy zmieniły się w TRAKCIE okresu. Liczymy cały miesiąc po
   * warunkach obowiązujących na jego koniec — podziału proporcjonalnego między
   * stare i nowe wynagrodzenie silnik nie robi.
   */
  'zmiana_warunkow_w_okresie',
  /**
   * Potrącenia z art. 87 k.p. nie zmieściły się w granicach albo w kwocie
   * wolnej (Etap 33). Tytuł nie znika — kadry muszą wiedzieć, co powiedzieć
   * komornikowi.
   */
  'potracenie_ograniczone',
  /**
   * Zajęcie sięga zasiłku, ale kwoty wolnej z art. 141 ustawy emerytalnej nie
   * ma w parametrach roku (ogłasza ją komunikat Prezesa ZUS, waloryzowana od
   * 1 marca). Z zasiłku nie potrącamy wtedy niczego, zamiast liczyć od kwoty
   * wolnej wziętej z powietrza (Etap 36, ADR-0037).
   */
  'brak_kwoty_wolnej_zasilku',
  /**
   * Miesiąc bez rozkładu czasu pracy. Art. 151 § 1 k.p. liczy nadgodziny od
   * dobowego wymiaru wynikającego z ROZKŁADU — bez niego zostaje deklaracja
   * z ewidencji, więc podziału na dodatek 50 % i 100 % nie robimy
   * (Etap 42, ADR-0043).
   */
  'nadgodziny_z_deklaracji',
  /**
   * Wpisy ewidencji bez godzin rozpoczęcia i zakończenia pracy. Pory nocnej
   * nie da się z nich wyznaczyć oknem z art. 151(7) § 1, więc godziny nocne
   * bierzemy z rodzaju wpisu (Etap 42, ADR-0043).
   */
  'godziny_bez_zegara',
  /**
   * Czas wolny w zamian za nadgodziny udzielony przez pracodawcę PO okresie
   * rozliczeniowym. Art. 151(2) § 2 daje na to czas najpóźniej do jego końca,
   * więc dodatek pozostaje należny — kompensata czasem wolnym nie doszła do
   * skutku (Etap 43, ADR-0044).
   */
  'czas_wolny_po_terminie',
  /**
   * Roczny limit nadgodzin z art. 151 § 3 k.p. (albo inna liczba ustalona
   * w trybie § 4) został przekroczony. Nadgodziny i tak wynagradzamy —
   * przekroczenie granicy jest wykroczeniem z art. 281 § 1 pkt 5, nie powodem
   * do niezapłacenia (Etap 45, ADR-0046).
   */
  'limit_nadgodzin_roczny',
  /**
   * Przeciętny tygodniowy czas pracy z nadgodzinami przekroczył 48 godzin
   * w okresie rozliczeniowym (art. 131 § 1 k.p.). Ograniczenie nie dotyczy
   * pracowników zarządzających zakładem (§ 2) — tych oznacza się w granicach
   * pracownika (Etap 45, ADR-0046).
   */
  'limit_48_godzin',
  /**
   * Nierezydent ze stawką z umowy o unikaniu podwójnego opodatkowania, ale bez
   * certyfikatu rezydencji obejmującego dzień wypłaty. Art. 29 ust. 2 pozwala
   * zastosować stawkę umowną WYŁĄCZNIE na podstawie certyfikatu, więc liczymy
   * ustawowe 20 % i mówimy o tym wprost (Etap 48, ADR-0049).
   */
  'nierezydent_bez_certyfikatu',
  /**
   * Pasek nierezydenta z zasiłkiem. Podatek u źródła z art. 29 ust. 1 pkt 1
   * obejmuje przychody z art. 13 pkt 2 i 6–9 — zasiłku nie, bo to przychód
   * z innych źródeł. Kwoty zasiłku nie opodatkowujemy ryczałtem ani zaliczką:
   * jej rozliczenie zostaje do sprawdzenia przez człowieka (Etap 48).
   */
  'nierezydent_zasilek_poza_ryczaltem',
  /**
   * Praca w niedzielę albo święto bez dnia wolnego udzielonego w terminie
   * z art. 151(11) § 1. Dodatek 100 % za te godziny wchodzi na listę płac
   * (§ 2 i § 3) — ostrzeżenie mówi, ile godzin i za które dni, bo dnia wolnego
   * wciąż wolno udzielić do końca okresu (Etap 49, ADR-0050).
   */
  'niedziela_bez_dnia_wolnego',
  /**
   * Cztery przepracowane niedziele z rzędu. Art. 151(12) k.p. każe zapewnić
   * pracownikowi niedzielę wolną co najmniej raz na cztery tygodnie —
   * naruszenie jest wykroczeniem z art. 281 § 1 pkt 5, a nie powodem do
   * niezapłacenia (Etap 49, ADR-0050).
   */
  'cztery_niedziele_z_rzedu',
  /**
   * Dyżur poza domem razem z pracą zajął więcej niż 13 godzin doby, więc
   * zabrał godziny odpoczynku dobowego (art. 151(5) § 2 w związku
   * z art. 132 § 1 k.p.). Wynagrodzenie za dyżur liczy się normalnie —
   * naruszenie odpoczynku jest wykroczeniem, nie powodem do niezapłacenia
   * (Etap 50, ADR-0051).
   */
  'dyzur_narusza_odpoczynek',
  /**
   * Ryczałt za nadgodziny jest niższy od kwoty, która wyszłaby z godzin.
   * Art. 151(1) § 4 chce, żeby odpowiadał PRZEWIDYWANEMU wymiarowi pracy
   * nadliczbowej — a przy trwałym niedoszacowaniu pracownikowi należy się
   * różnica. System jej nie dopłaca sam: nazywa ją (Etap 51, ADR-0052).
   */
  'ryczalt_nizszy_od_nadgodzin',
  /** To samo przy ryczałcie za pracę w nocy (art. 151(8) § 2) — Etap 51. */
  'ryczalt_nocny_nizszy',
  /**
   * Ewidencja pokazuje dobę pracowniczą (art. 128 § 3 pkt 1) z odpoczynkiem
   * krótszym niż 11 godzin (art. 132 § 1 k.p.). Zapłata się nie zmienia —
   * naruszenie jest wykroczeniem z art. 281 § 1 pkt 5, jak przy limitach
   * nadgodzin — a pracownika zarządzającego zakładem to nie dotyczy
   * (art. 132 § 2 pkt 1). Fala 7b, grupa 175.
   */
  'odpoczynek_dobowy_naruszony',
  /**
   * Wpis dyżuru niesie pracę, której nie ma w ewidencji: praca w czasie dyżuru
   * rozlicza się jak praca (art. 151(5) § 2 zd. 1), ale do czasu pracy wchodzi
   * dopiero wpisem w ewidencji — bez niego godziny wypadają z gotowości i nie
   * są płacone jako praca (ADR-0051 pkt 2). Fala 7b, grupa 50.
   */
  'dyzur_praca_poza_ewidencja',
] as const
export type PayrollWarningKind = (typeof PAYROLL_WARNING_KINDS)[number]

/** Uczestnictwo w PPK (ustawa o PPK): bez deklaracji naliczamy stawki domyślne. */
export const PPK_STATUSES = ['uczestnik', 'rezygnacja', 'brak_deklaracji'] as const
export type PpkStatus = (typeof PPK_STATUSES)[number]

/** Koszty uzyskania przychodu (art. 22 ust. 2 ustawy o PIT): miejscowe lub podwyższone. */
/**
 * Koszty autorskie: 50 % przychodu przy korzystaniu z praw autorskich albo
 * rozporządzaniu nimi (art. 22 ust. 9 pkt 3 ustawy o PIT). Roczna suma kosztów
 * z pkt 1–3 nie może przekroczyć górnej granicy pierwszego progu skali
 * (art. 22 ust. 9a) — u nas to ten sam parametr roku, którym liczymy próg
 * podatkowy (Etap 46, ADR-0047).
 */
export const AUTHOR_KUP_BP = 5000

export const KUP_VARIANTS = ['podstawowe', 'podwyzszone'] as const
export type KupVariant = (typeof KUP_VARIANTS)[number]

/**
 * Podzielnik kwoty zmniejszającej z PIT-2 (art. 31b ustawy o PIT): 1/12, 1/24
 * albo 1/36 rocznej kwoty; 0 = pracownik nie złożył PIT-2 u tego płatnika.
 */
export const TAX_CREDIT_DIVISORS = [0, 12, 24, 36] as const
export type TaxCreditDivisor = (typeof TAX_CREDIT_DIVISORS)[number]

export function isTaxCreditDivisor(value: unknown): value is TaxCreditDivisor {
  return typeof value === 'number' && (TAX_CREDIT_DIVISORS as readonly number[]).includes(value)
}

/** Status paska w portalu (spec 03 §380) — pochodna statusu okresu, nie kolumna. */
export const PAYSLIP_STATUSES = ['draft', 'paid'] as const
export type PayslipStatus = (typeof PAYSLIP_STATUSES)[number]

/** Ustawowe stawki i limity roku — konfiguracja wersjonowana, nie stałe w kodzie. */
export interface PayrollYearParameters {
  readonly year: number
  /** Emerytalna (art. 22 ust. 1 pkt 1 ustawy o systemie ubezpieczeń społecznych). */
  readonly pensionEmployeeBp: RateBp
  readonly pensionEmployerBp: RateBp
  /** Rentowa: 1,5 % pracownik, 6,5 % pracodawca (art. 22 ust. 1 pkt 2). */
  readonly disabilityEmployeeBp: RateBp
  readonly disabilityEmployerBp: RateBp
  /** Chorobowa pracownika — przy zleceniu dobrowolna. */
  readonly sicknessEmployeeBp: RateBp
  /** Fundusz Pracy i Fundusz Solidarnościowy (łącznie). */
  readonly laborFundBp: RateBp
  /** FGŚP. */
  readonly guaranteedBenefitsBp: RateBp
  /** Zdrowotna (art. 79 ustawy o świadczeniach opieki zdrowotnej). */
  readonly healthBp: RateBp
  /** PIT: pierwszy i drugi próg. */
  readonly taxFirstRateBp: RateBp
  readonly taxSecondRateBp: RateBp
  /** Granica pierwszego progu w groszach (dochód narastająco). */
  readonly taxThresholdGr: number
  /** Roczna kwota zmniejszająca podatek w groszach (dzielona przez PIT-2). */
  readonly taxCreditAnnualGr: number
  /** Koszty uzyskania: miejscowe i podwyższone, miesięcznie w groszach. */
  readonly kupBasicGr: number
  readonly kupRaisedGr: number
  /** Koszty przy umowie zlecenia — procent przychodu po składkach. */
  readonly mandateKupBp: RateBp
  /** Ulga dla młodych: limit roczny przychodu (art. 21 ust. 1 pkt 148). */
  readonly youthReliefLimitGr: number
  /** Domyślne stawki PPK: 2 % pracownik, 1,5 % pracodawca. */
  readonly ppkEmployeeDefaultBp: RateBp
  readonly ppkEmployerDefaultBp: RateBp
  /**
   * Roczna podstawa emerytalnej i rentowej („30-krotność", art. 19 ust. 1
   * ustawy o systemie ubezpieczeń społecznych) w groszach. `null` = kwota na
   * dany rok nie jest wpisana; silnik liczy wtedy bez ograniczenia i mówi o tym.
   */
  readonly annualSocialCapGr: number | null
  /** Minimalne wynagrodzenie w groszach; `null` = niewpisane. */
  readonly minimumWageGr: number | null
  /**
   * Kwoty wolne od potrąceń z ZASIŁKÓW (art. 141 ust. 1 ustawy o emeryturach
   * i rentach z FUS), osobno dla alimentów i dla innych należności
   * egzekwowanych, w groszach. To kwoty złotowe ogłaszane komunikatem Prezesa
   * ZUS i waloryzowane od 1 MARCA — nie procent minimalnej płacy, jak przy
   * wynagrodzeniu. Parametr trzyma wartość obowiązującą od 1 marca tego roku;
   * `null` = nie wpisano, więc z zasiłku nie potrącamy niczego i mówimy o tym
   * ostrzeżeniem (Etap 36, ADR-0037).
   */
  readonly benefitFreeAlimonyGr: number | null
  readonly benefitFreeOtherGr: number | null
  /**
   * Zryczałtowany podatek od umów, w których kwota należności OKREŚLONA
   * W UMOWIE nie przekracza progu (art. 30 ust. 1 pkt 5a ustawy o PIT).
   * Stawka i próg są parametrami roku, bo jedno i drugie zmieniały się ustawami:
   * 17 % przed reformą z 1 lipca 2022 r., próg 200 zł od 2007 r. (Etap 24).
   */
  readonly flatRateBp: RateBp
  readonly flatRateThresholdGr: number
  /** Dodatek za pracę w nocy: procent stawki godzinowej z minimalnego (art. 151[8] k.p.). */
  readonly nightAllowanceBp: RateBp
  /** Dodatek za nadgodziny: 50 % (dzień) i 100 % (noc, niedziele, święta). */
  readonly overtimeDayBp: RateBp
  readonly overtimeNightBp: RateBp
  /**
   * Reguła z art. 83 ust. 1 ustawy o świadczeniach opieki zdrowotnej: składka
   * zdrowotna nie może przekroczyć zaliczki PIT policzonej wg stanu prawnego
   * na 31.12.2021. Stałe historyczne — stawka 17 % i roczna kwota zmniejszająca
   * 525,12 zł. Odliczenia zdrowotnej w 7,75 % (uchylony art. 27b PIT) w tym
   * rachunku NIE ma: to ona jest przedmiotem porównania, więc jej uwzględnienie
   * byłoby błędnym kołem (patrz ADR-0010).
   */
  readonly health2021TaxRateBp: RateBp
  readonly health2021AnnualCreditGr: number
  /**
   * Absencja chorobowa (ADR-0012). Stawki: 80 % podstawy (art. 92 § 1 pkt 1
   * k.p. i art. 11 ust. 1 ustawy zasiłkowej) oraz 100 % w przypadkach
   * wskazanych ustawą (wypadek w drodze, ciąża, dawca, wypadek przy pracy).
   */
  readonly sickPayRateBp: RateBp
  readonly sickPayFullRateBp: RateBp
  /** Dni wynagrodzenia chorobowego w roku: 33 oraz 14 po roku ukończenia 50 lat. */
  readonly sickPayDaysLimit: number
  readonly sickPaySeniorDaysLimit: number
  readonly sickPaySeniorAge: number
  /** Dzielnik podstawy dziennej — jeden miesiąc to 30 dni (art. 11 ust. 5). */
  readonly sickDailyDivisor: number
  /** Miesiące historii do przeciętnego wynagrodzenia (art. 36 ust. 1). */
  readonly sickBaseMonths: number
  /** Okres wyczekiwania na prawo do zasiłku w dniach (art. 4 ust. 1 pkt 1). */
  readonly sickWaitingDays: number
  /** Okres zasiłkowy w dniach (art. 8) — próg ostrzeżenia, nie decyzja silnika. */
  readonly benefitPeriodDays: number
  /**
   * Wynagrodzenie płatnika za wypłatę zasiłków: 0,1 % kwoty prawidłowo
   * wypłaconych świadczeń (rozporządzenie MPiPS z 14.12.1998). Potrącane
   * z należnych składek — blok V deklaracji ZUS DRA.
   */
  readonly benefitPayerFeeBp: RateBp
  /**
   * Ustawowe terminy przekazania dokumentów (dzień miesiąca NASTĘPNEGO po
   * rozliczanym): zaliczki PIT-4 do 20., wpłaty PPK do 15. Termin składek ZUS
   * zależy od rodzaju płatnika (art. 47 ust. 1 u.s.u.s. od 1.01.2022):
   * `zusDeadlineDay` to pkt 4 (pozostali, 20.), `zusDeadlineDayLegalPerson`
   * pkt 3 (osobowość prawna, 15.), `zusDeadlineDayBudget` pkt 2 (jednostki
   * budżetowe, 5.). Który obowiązuje, mówi kod terminu płatnika
   * (`zusDeadlineDayFor`, #10). Dni są parametrem roku, bo zmienia je ustawa.
   */
  readonly zusDeadlineDay: number
  readonly zusDeadlineDayBudget: number
  readonly zusDeadlineDayLegalPerson: number
  readonly pitDeadlineDay: number
  readonly ppkDeadlineDay: number
  /** Czy wartości potwierdziła księgowość (patrz ADR-0010). */
  readonly confirmed: boolean
  /** Podstawa prawna — pokazywana w konfiguracji, żeby dało się zweryfikować. */
  readonly legalBasis: string
}

/** Ustawienia płacowe firmy: składka wypadkowa zależy od PKD i zatrudnienia. */
export interface TenantPayrollSettings {
  /** Wypadkowa pracodawcy — ustala ją ZUS dla płatnika, nie jest stała. */
  readonly accidentBp: RateBp | null
  /** Dzień wypłaty w miesiącu (spec 03: „NAJBLIŻSZA WYPŁATA … 28 czerwca"). */
  readonly payoutDay: number
  /** Czy wypłata przypada w miesiącu następnym (wynagrodzenie z dołu). */
  readonly payoutNextMonth: boolean
  /** Próg odchylenia brutto m/m, po którym pozycja trafia do „Nietypowych" (bp). */
  readonly deviationAlertBp: RateBp
  /**
   * Czy firma sama ustala prawo do zasiłków i je wypłaca. Obowiązek ma płatnik,
   * który 30 listopada poprzedniego roku zgłaszał do ubezpieczenia chorobowego
   * WIĘCEJ NIŻ 20 ubezpieczonych (art. 61 ust. 1 pkt 1 ustawy zasiłkowej).
   * Liczby nie zgadujemy z bieżącego stanu kadr — to decyzja księgowości.
   */
  readonly benefitPayer: boolean
  /** Czy potrącać wynagrodzenie płatnika (0,1 % wypłaconych zasiłków). */
  readonly benefitPayerFee: boolean
}

/** Ustawienia płacowe pracownika — realne deklaracje, nie domysły silnika. */
/**
 * Ulgi podatkowe, które płatnik stosuje na WNIOSEK pracownika — obok ulgi dla
 * młodych, która działa z mocy wieku (Etap 32, ADR-0033).
 *
 *  - **powrot** — ulga na powrót (art. 21 ust. 1 pkt 152): przychody podatnika,
 *    który przeniósł miejsce zamieszkania na terytorium Polski, przez CZTERY
 *    kolejno następujące po sobie lata podatkowe,
 *  - **rodzina_4** — ulga dla rodzin 4+ (pkt 153): podatnik, który w roku
 *    wykonywał władzę rodzicielską wobec co najmniej czworga dzieci,
 *  - **senior** — ulga dla pracujących seniorów (pkt 154): kobieta po 60.,
 *    mężczyzna po 65. roku życia, podlegający ubezpieczeniom i NIEPOBIERAJĄCY
 *    emerytury.
 *
 * Wszystkie trzy obejmują te same źródła co ulga dla młodych: stosunek pracy,
 * umowy zlecenia z art. 13 pkt 8 i zasiłek macierzyński.
 *
 * **Limit jest JEDEN i wspólny z ulgą dla młodych**: „limit zwolnienia od
 * podatku przychodów wykazanych w poz. 109 i 114 wynosi ŁĄCZNIE 85 528 zł
 * w roku podatkowym" (przypis 22 formularza PIT-11). Dlatego w silniku obie
 * ulgi czerpią z tej samej puli, a różnią się wyłącznie tym, w której pozycji
 * formularza wychodzą.
 *
 * O tym, czy warunki są spełnione, system NIE orzeka: nie wie, czy senior
 * pobiera emeryturę ani ile dzieci wychowuje podatnik. Podstawą jest pisemne
 * oświadczenie pracownika (art. 31a ust. 8 PIT) — my zapisujemy je jako fakt.
 */
export const TAX_RELIEF_KINDS = ['powrot', 'rodzina_4', 'senior'] as const
export type TaxReliefKind = (typeof TAX_RELIEF_KINDS)[number]

/**
 * Pozycja formularza PIT-11, w której zaznacza się podstawę prawną zwolnienia
 * wykazanego w poz. 114: P_118 dla art. 21 ust. 1 pkt 152, P_119 dla pkt 153,
 * P_120 dla pkt 154.
 */
export const TAX_RELIEF_POSITIONS: Record<TaxReliefKind, number> = {
  powrot: 118,
  rodzina_4: 119,
  senior: 120,
}

/**
 * Czy ulga dla pracujących seniorów. Ma to znaczenie NIE tylko dla pozycji
 * zwolnienia: przychód opodatkowany osoby po wieku emerytalnym wykazuje się
 * w wierszu 3 formularza (a zlecenie w wierszu 8), nie w wierszu 1.
 */
export function isSeniorRelief(kind: TaxReliefKind | null | undefined): boolean {
  return kind === 'senior'
}

export interface EmployeePayrollProfile {
  readonly ppkStatus: PpkStatus
  readonly ppkEmployeeBp: RateBp | null
  readonly ppkEmployerBp: RateBp | null
  readonly kupVariant: KupVariant
  /** `null` = brak jawnej deklaracji; silnik bierze wtedy pozycję akt „PIT-2". */
  readonly taxCreditDivisor: TaxCreditDivisor | null
  /** Ulga dla młodych wyłączona na wniosek pracownika (art. 32 ust. 1b PIT). */
  readonly youthReliefWaived: boolean
  /**
   * Ulga z art. 21 ust. 1 pkt 152–154 zadeklarowana przez pracownika (Etap 32).
   * `null` = brak oświadczenia. Limit jest wspólny z ulgą dla młodych.
   */
  readonly taxReliefKind: TaxReliefKind | null
  /**
   * Ostatni rok, w którym ulga obowiązuje — ulga na powrót przysługuje przez
   * cztery kolejne lata podatkowe, a lata wskazuje oświadczenie pracownika.
   * `null` = bez ograniczenia roku (rodziny 4+ i seniorzy).
   */
  readonly taxReliefUntilYear: number | null
  /** Zlecenie: chorobowa jest dobrowolna — liczymy ją tylko po zgłoszeniu. */
  readonly mandateSicknessOptIn: boolean
  /** Zlecenie: uczeń lub student do 26 lat — bez składek ZUS (art. 6 ust. 4). */
  readonly mandateStudentExempt: boolean
  /**
   * Zlecenie: zakres ubezpieczenia z tej umowy (ADR-0026 §1, #80). „Tylko
   * zdrowotne" — społeczne z innego tytułu (zbieg tytułów, art. 9 u.s.u.s.):
   * zero składek społecznych, FP, FGŚP i PPK, zdrowotna od całego przychodu.
   * Przy innych umowach i przy studencie silnik zakres pomija.
   */
  readonly insuranceScope: InsuranceScope
  /**
   * Koszty autorskie 50 % zamiast 20 % (art. 22 ust. 9 pkt 3 PIT). Dotyczy
   * umów, w których wynagrodzenie obejmuje przeniesienie praw autorskich do
   * utworu albo korzystanie z nich — o tym, czy tak jest, rozstrzyga treść
   * umowy, nie system (Etap 46, ADR-0047).
   */
  readonly authorCosts: boolean
  /**
   * Dni wynagrodzenia chorobowego wykorzystane w tym roku U POPRZEDNIEGO
   * pracodawcy — pula 33 (14) dni z art. 92 k.p. jest roczna i wspólna dla
   * wszystkich pracodawców. Liczba pochodzi ze świadectwa pracy; system jej
   * nie zna i nie udaje, że zna.
   */
  readonly sickPayDaysBefore: number
  /** Rok, którego dotyczy liczba dni ze świadectwa pracy; `null` = niewpisany. */
  readonly sickPayDaysBeforeYear: number | null
  /**
   * Prawo do świadczenia od pierwszego dnia ubezpieczenia — okres wyczekiwania
   * z art. 4 ust. 1 nie obowiązuje (absolwent w ciągu 90 dni, co najmniej
   * 10 lat obowiązkowego ubezpieczenia, poprzednie ubezpieczenie z przerwą
   * do 30 dni). Kadry zaznaczają to na podstawie dokumentów.
   */
  readonly benefitFromFirstDay: boolean
  /**
   * Rezydencja podatkowa (art. 3 ust. 1a i ust. 2a PIT). Nierezydent płaci
   * podatek u źródła od przychodów z art. 13 pkt 2 i 6–9 (art. 29 ust. 1
   * pkt 1), a nie zaliczkę według skali. O rezydencji rozstrzyga ośrodek
   * interesów życiowych albo 183 dni pobytu — tego lista płac nie wie, więc
   * jest to fakt z akt, tak samo jak PIT-2 (Etap 48, ADR-0049).
   */
  readonly taxResidence: TaxResidence
  /** Kod kraju rezydencji (ISO 3166-1 alpha-2) — pole „Kraj" w IFT-1R. */
  readonly residenceCountry: string | null

  /**
   * Okres, który obejmuje certyfikat rezydencji. Bez ważnego certyfikatu
   * stawka z umowy o unikaniu podwójnego opodatkowania nie wchodzi w grę
   * (art. 29 ust. 2) — wraca ustawowe 20 %.
   */
  readonly residenceCertificateFrom: string | null
  readonly residenceCertificateTo: string | null
  /** Stawka z umowy o UPO w punktach bazowych; null = brak ustaleń. */
  readonly treatyTaxBp: number | null
}

export const DEFAULT_EMPLOYEE_PAYROLL_PROFILE: EmployeePayrollProfile = {
  ppkStatus: 'brak_deklaracji',
  ppkEmployeeBp: null,
  ppkEmployerBp: null,
  kupVariant: 'podstawowe',
  taxCreditDivisor: null,
  youthReliefWaived: false,
  taxReliefKind: null,
  taxReliefUntilYear: null,
  mandateSicknessOptIn: false,
  mandateStudentExempt: false,
  insuranceScope: 'pelne',
  authorCosts: false,
  sickPayDaysBefore: 0,
  sickPayDaysBeforeYear: null,
  benefitFromFirstDay: false,
  taxResidence: 'rezydent',
  residenceCountry: null,
  residenceCertificateFrom: null,
  residenceCertificateTo: null,
  treatyTaxBp: null,
}

/** Wiek, do którego przysługuje ulga dla młodych (art. 21 ust. 1 pkt 148 PIT). */
export const YOUTH_RELIEF_MAX_AGE = 26

// ---------------------------------------------------------------------------
// Kontrakty API (§7 blok PAYROLL)
// ---------------------------------------------------------------------------

export interface PayrollWarningView {
  readonly id: string
  readonly kind: PayrollWarningKind
  readonly level: PayrollWarningLevel
  /** Pracownik, którego dotyczy pozycja; `null` = ostrzeżenie całego okresu. */
  readonly employeeId: string | null
  readonly employeeName: string | null
  /** Parametry do interpolacji w kluczu i18n (grosze albo liczby). */
  readonly params: Readonly<Record<string, string | number>>
}

/**
 * Miano liczby jednostek składnika — stałe dla rodzaju (silnik zapisuje godziny
 * pracy albo dni nieobecności). Jedna mapa dla ekranu listy płac i paska PDF,
 * żeby dni nie wychodziły jako „h" (grupa 62).
 */
export const PAYROLL_COMPONENT_UNITS: Readonly<Record<PayrollComponentKind, 'hours' | 'days' | null>> = {
  wynagrodzenie_zasadnicze: null,
  premia: null,
  nadgodziny: 'hours',
  nadgodziny_100: 'hours',
  dodatek_nocny: 'hours',
  ryczalt_nadgodziny: null,
  ryczalt_nocny: null,
  dodatek_niedzielny: 'hours',
  wynagrodzenie_za_dyzur: 'hours',
  wynagrodzenie_chorobowe: 'days',
  zasilek_chorobowy: 'days',
  zasilek_macierzynski: 'days',
  potracenie_chorobowe: 'days',
  potracenie_nieobecnosc: 'days',
  faktura_b2b: null,
  ekwiwalent: null,
  potracenie_brutto: null,
  odprawa: null,
}

export interface PayrollComponentView {
  readonly kind: PayrollComponentKind
  readonly amountGr: number
  /** Liczba jednostek (godziny albo dni — miano w PAYROLL_COMPONENT_UNITS); `null` gdy nie dotyczy. */
  readonly units: number | null
  readonly note: string | null
}

export interface PayslipSummary {
  readonly id: string
  readonly employeeId: string
  readonly employeeName: string
  readonly employeeNo: number
  readonly contractType: string | null
  readonly grossGr: number
  readonly netGr: number
  readonly employerCostGr: number
  readonly status: PayslipStatus
}

export interface PayslipDetail extends PayslipSummary {
  readonly month: string
  readonly components: readonly PayrollComponentView[]
  readonly socialEmployeeGr: number
  readonly pensionEmployeeGr: number
  readonly disabilityEmployeeGr: number
  readonly sicknessEmployeeGr: number
  readonly healthGr: number
  readonly taxAdvanceGr: number
  readonly taxBaseGr: number
  readonly kupGr: number
  readonly taxCreditGr: number
  readonly ppkEmployeeGr: number
  readonly ppkEmployerGr: number
  readonly socialEmployerGr: number
  readonly pensionEmployerGr: number
  readonly disabilityEmployerGr: number
  readonly accidentGr: number
  readonly laborFundGr: number
  readonly guaranteedBenefitsGr: number
  readonly payoutDate: string | null
  /** Wynagrodzenie chorobowe ze środków pracodawcy (art. 92 k.p.). */
  readonly sickPayGr: number
  /** Zasiłek chorobowy wypłacony przez płatnika; 0 gdy wypłaca go ZUS. */
  readonly benefitGr: number
  readonly sickDays: number
  readonly benefitDays: number
  /** Okresy niezdolności z kodami ZUS — to samo, co pójdzie do raportu ZUS RSA. */
  readonly absences: readonly SickAbsenceView[]
}

export interface PayrollPeriodSummary {
  readonly id: string
  /** Miesiąc PRZEPRACOWANY (rrrr-mm) — okres, za który liczy się lista. */
  readonly month: string
  /**
   * Miesiąc WYPŁATY (rrrr-mm) — miesiąc rozliczeniowy podatku i składek, po
   * którym idą deklaracje ZUS/PIT/PPK oraz limity roczne (ADR-0013).
   */
  readonly settlementMonth: string
  /** Realna data wypłaty zapisana przy okresie (art. 85 § 2 i § 3 k.p.). */
  readonly payoutDate: string
  readonly label: string | null
  readonly status: string
  readonly peopleCount: number
  readonly employerCostGr: number | null
  readonly warningCount: number
  readonly calculatedAt: string | null
}

/** Wiersz sekcji „Składniki wynagrodzeń" — agregat całego okresu. */
export interface PayrollComponentTotal {
  readonly kind: PayrollComponentKind
  readonly amountGr: number
  readonly peopleCount: number
  readonly units: number | null
}

export interface PayrollPeriodDetail extends PayrollPeriodSummary {
  readonly grossGr: number
  readonly netGr: number
  readonly socialEmployerGr: number
  readonly ppkEmployerGr: number
  /** Podział zatrudnienia do KPI „142 UoP · 6 zlecenie / B2B" (spec 01 §8b). */
  readonly employmentSplit: Readonly<Record<string, number>>
  readonly previousEmployerCostGr: number | null
  readonly componentTotals: readonly PayrollComponentTotal[]
  readonly warnings: readonly PayrollWarningView[]
  readonly payslips: readonly PayslipSummary[]
  readonly parametersConfirmed: boolean
  readonly parametersYear: number | null
  /**
   * Ile razy okres wrócił z zamkniętej listy do weryfikacji (Etap 13).
   * 0 = lista liczona raz; więcej = ekran mówi, że to korekta.
   */
  readonly reopenCount: number
  /** Powód ostatniego ponownego otwarcia — z audytu, nie z domysłu. */
  readonly reopenReason: string | null
  readonly reopenedAt: string | null
}

/**
 * Umowa o dzieło nie jest tytułem do ubezpieczeń: nie wymienia jej ani art. 6
 * ust. 1 ustawy o systemie ubezpieczeń społecznych, ani art. 66 ustawy
 * o świadczeniach opieki zdrowotnej. Zero składek społecznych, zero zdrowotnej
 * i zero PPK — bo podstawą wpłat PPK jest podstawa składek emerytalno-rentowych
 * (Etap 46, ADR-0047).
 */
export function isContributionFreeContract(type: ContractType): boolean {
  return type === 'dzielo'
}

/**
 * Czy osobę w ogóle obejmuje PPK (grupa 101). Zleceniobiorca jest „osobą
 * zatrudnioną" w rozumieniu ustawy o PPK tylko wtedy, gdy OBOWIĄZKOWO podlega
 * ubezpieczeniom emerytalnemu i rentowym (art. 2 ust. 1 pkt 18 lit. d ustawy
 * o PPK, warunek z części wspólnej pkt 18) — student do 26 lat im nie podlega (art. 6 ust. 4 u.s.u.s.), dzieło
 * nie jest tytułem do ubezpieczeń (ADR-0047), a B2B nie jest listą płac.
 *
 * Jedna reguła dla silnika, licznika ostrzeżenia i kwoty wolnej od potrąceń:
 * ostrzeżenie „brak deklaracji PPK" mówiło o wpłatach osobie, której silnik
 * niczego nie naliczył, a kwota wolna odejmowała jej PPK, którego nie było.
 */
export function isPpkCovered(
  type: ContractType,
  profile: Pick<EmployeePayrollProfile, 'mandateStudentExempt'> & {
    readonly insuranceScope?: EmployeePayrollProfile['insuranceScope']
  },
): boolean {
  if (type === 'b2b') return false
  if (isEmploymentContract(type)) return true
  /* Zlecenie „tylko zdrowotne" nie podlega obowiązkowo emerytalnemu i rentowym (#80). */
  if (isHealthOnlyMandate(type, profile)) return false
  return !profile.mandateStudentExempt && !isContributionFreeContract(type)
}

/**
 * Zlecenie o zakresie „tylko zdrowotne" (#80, ADR-0026 §1): ubezpieczenia
 * społeczne zleceniobiorca ma z innego tytułu (zbieg tytułów, art. 9 u.s.u.s.),
 * więc z tej umowy nie ma składek społecznych, FP, FGŚP ani PPK, a zdrowotna
 * liczy się od całego przychodu — nie ma składek społecznych do odliczenia
 * (art. 81 u.ś.o.z.). Student do 26 lat nie ma żadnego tytułu, więc go nie
 * dotyczy. Reguły do potwierdzenia przez księgowość (ADR-0010).
 */
export function isHealthOnlyMandate(
  type: ContractType,
  profile: Pick<EmployeePayrollProfile, 'mandateStudentExempt'> & {
    readonly insuranceScope?: EmployeePayrollProfile['insuranceScope']
  },
): boolean {
  return (
    type === 'zlecenie' && !profile.mandateStudentExempt && profile.insuranceScope === 'tylko_zdrowotne'
  )
}

/**
 * Kwota do wypłaty z paska — jedna reguła dla pliku przelewów, PDF paska
 * i pulpitu portalu (fala 8, grupa 20). Potrącenia z art. 87 § 1 k.p.
 * zmniejszają to, co idzie na rachunek pracownika (ADR-0034, ADR-0035 pkt 1),
 * a zwrot kary z art. 112 § 3 k.p. wraca po stronie netto (ADR-0039 § 6).
 * Wynik nigdy nie jest ujemny.
 */
export function payoutGr(slip: {
  readonly netGr: number
  readonly garnishmentGr?: number | null
  readonly penaltyRefundGr?: number | null
}): number {
  return Math.max(0, slip.netGr - (slip.garnishmentGr ?? 0) + (slip.penaltyRefundGr ?? 0))
}
